import React, { FC, useEffect, useMemo } from 'react';

import { Content, List, Root, Trigger } from '@radix-ui/react-tabs';
import { NextPage } from 'next';

import { Breadcrumb, BreadcrumbWithUrl } from '@domains/breadcrumbs';
import { Tabs } from '@domains/tabs';
import { ProductPageProps } from '@domains/view';

import { sendShowProductDetailsEvent } from '@utils/analytics';
import { APP_URL } from '@utils/env';
import {
    filterAttributes,
    getAttributesEntries,
    getBooleanShortName,
    getFormattedAttributeName,
    NAME_IS_READY_TO_EAT,
} from '@utils/product/attributes';
import { makeCatalogRoute, makeCategoryRoute } from '@utils/route';
import { makeMetaDescriptionForProduct } from '@utils/seo';

import { DefaultLayout } from '@layouts/DefaultLayout/DefaultLayout';

import { Breadcrumbs } from '@components/common/Breadcrumbs/Breadcrumbs';
import { Container } from '@components/common/Container/Container';
import { RecipesSlider } from '@components/common/RecipesSlider/RecipesSlider';
import { ProductSchema } from '@components/common/SchemaOrg/ProductSchema';
import { WysiwygPresenter } from '@components/common/WysiwygPresenter/WysiwygPresenter';
import { ProductGallerySlider } from '@components/product/ProductGallerySlider/ProductGallerySlider';
import { ProductInfoBlock } from '@components/product/ProductInfoBlock/ProductInfoBlock';

import styles from './ProductView.module.scss';

const Characteristic: FC<{ name: string; value: string }> = ({ name, value }) => (
    <li className={styles.characteristic}>
        <span className={styles.characteristicLabel}>{name}</span>
        <span className={styles.characteristicValue} itemProp="value">
            {value}
        </span>
    </li>
);

export const ProductView: NextPage<ProductPageProps> = (props) => {
    const { data } = props;
    const { product, recipes, parent_category } = data;

    const {
        name,
        primary_image,
        gallery,
        description,
        measure_unit,
        attributes,
        is_ready_to_eat,
        seo,
        vendor_code,
        is_available,
    } = product;

    const breadcrumbs: Breadcrumb[] = [
        {
            caption: 'Каталог',
            url: makeCatalogRoute(),
        },
    ];

    if (parent_category !== null) {
        breadcrumbs.push({
            caption: parent_category.name,
            url: makeCategoryRoute(parent_category.slug),
        });
    }

    breadcrumbs.push({
        caption: name,
    });

    const productSeo = { ...seo, description: makeMetaDescriptionForProduct(name, measure_unit) };

    const tabs: Tabs = useMemo(() => {
        const result: Tabs = [];

        if (description !== null) {
            result.push({
                value: 'description',
                title: 'Описание',
                content: (
                    <WysiwygPresenter className={styles.descriptionText} content={description} />
                ),
            });
        }

        const attributeIsReadyToEat = [NAME_IS_READY_TO_EAT, getBooleanShortName(is_ready_to_eat)];
        const filteredAttributes = filterAttributes(attributes);
        const attributesEntries = getAttributesEntries(filteredAttributes);

        result.push({
            value: 'characteristics',
            title: 'Характеристики',
            content: (
                <ul className={styles.characteristics}>
                    <Characteristic
                        name={attributeIsReadyToEat[0]}
                        value={attributeIsReadyToEat[1]}
                        key="is_ready_to_eat"
                    />

                    {attributesEntries.map((entries) => {
                        const formattedName = getFormattedAttributeName(entries[0]);
                        return (
                            <Characteristic
                                key={entries[0]}
                                name={formattedName}
                                value={entries[1]}
                            />
                        );
                    })}
                </ul>
            ),
        });

        return result;
    }, [attributes, description, is_ready_to_eat]);

    useEffect(() => {
        sendShowProductDetailsEvent(product);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const canonicalPath =
        parent_category !== null
            ? `${APP_URL}/catalog/${parent_category.slug}/${product.slug}/`
            : undefined;

    return (
        <DefaultLayout {...productSeo} canonicalPath={canonicalPath}>
            <ProductSchema
                name={name}
                description={description}
                image={primary_image}
                sku={vendor_code ?? ''}
                price={measure_unit.price_in_kopecks.new}
                oldPrice={measure_unit.price_in_kopecks.old}
                measureUnit={measure_unit.type.name}
                isAvailable={is_available}
            />
            <div className={styles.root} itemScope itemType="https://schema.org/Product">
                <Container>
                    <Breadcrumbs
                        items={breadcrumbs}
                        mobileButton={breadcrumbs[breadcrumbs.length - 2] as BreadcrumbWithUrl}
                    />

                    <div className={styles.content}>
                        <ProductGallerySlider
                            items={[primary_image, ...gallery]}
                            className={styles.gallery}
                        />
                        <ProductInfoBlock {...product} />
                    </div>

                    {tabs.length > 0 && (
                        <Root defaultValue={tabs[0].value} className={styles.tabs}>
                            <List className={styles.tabsList}>
                                {tabs.map((tab, index) => (
                                    <Trigger className={styles.tab} value={tab.value} key={index}>
                                        {tab.title}
                                    </Trigger>
                                ))}
                            </List>
                            {tabs.map((tab, index) => (
                                <Content
                                    className={styles.tabContent}
                                    value={tab.value}
                                    key={index}
                                    {...(tab.value === 'description'
                                        ? { itemProp: 'description' }
                                        : {})}
                                >
                                    {tab.content}
                                </Content>
                            ))}
                        </Root>
                    )}
                </Container>

                {/*
                TODO: integrate sliders
                <ProductsSlider
                    products={buy_with_this_product}
                    title="С этим товаром покупают"
                    className={styles.slider}
                />

                <ProductsSlider
                    products={similar_products}
                    title="Похожие товары"
                    className={styles.slider}
                />
                */}

                {recipes.length > 0 && (
                    <RecipesSlider
                        recipes={recipes}
                        title="Рецепты с этим товаром"
                        className={styles.slider}
                    />
                )}
            </div>
        </DefaultLayout>
    );
};
